import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchDuplicateGroup, mergeListings, RECORD_FIELDS } from '../recordsApi';
import { useAsync } from '../hooks';

const isBlank = (value) => value == null || String(value).trim() === '';

// "895000" and "895000.0" count as the same value
const sameValue = (a, b) => {
  if (isBlank(a) && isBlank(b)) return true;
  if (isBlank(a) || isBlank(b)) return false;
  const x = String(a).trim();
  const y = String(b).trim();
  if (x === y) return true;
  const nx = Number(x.replace(/[€,]/g, ''));
  const ny = Number(y.replace(/[€,]/g, ''));
  return !Number.isNaN(nx) && !Number.isNaN(ny) && nx === ny;
};

const show = (value) => (isBlank(value) ? <span className="rec-muted">—</span> : String(value));

function MergeForm({ eircode, listings }) {
  const navigate = useNavigate();
  const [keepId, setKeepId] = useState(listings[0].DaftId__c); // the newest listing
  const [picked, setPicked] = useState({}); // field -> id, only for explicit clicks
  const [pickedImage, setPickedImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const byId = useMemo(() => Object.fromEntries(listings.map((l) => [l.DaftId__c, l])), [listings]);
  const differs = (key) => listings.some((l) => !sameValue(l[key], listings[0][key]));

  // Untouched fields follow the kept record, falling back to the first listing that has a value
  const defaultSource = (key) => {
    if (!isBlank(byId[keepId][key])) return keepId;
    const other = listings.find((l) => !isBlank(l[key]));
    return other ? other.DaftId__c : keepId;
  };
  const sourceFor = (key) => (picked[key] && byId[picked[key]] ? picked[key] : defaultSource(key));

  const imageListings = listings.filter((l) => l.has_image);
  const defaultImage = byId[keepId].has_image ? keepId : imageListings[0] && imageListings[0].DaftId__c;
  const imageSource = pickedImage || defaultImage || null;

  const handleSave = async () => {
    const others = listings.filter((l) => l.DaftId__c !== keepId).map((l) => l.DaftId__c);
    if (!window.confirm(`Merge ${listings.length} listings into ${keepId}?\n\n${others.join(', ')} will be removed (a copy is kept in the merge archive).`)) return;
    setSaving(true);
    setError(null);
    try {
      const choices = {};
      RECORD_FIELDS.forEach((f) => { if (differs(f.key)) choices[f.key] = sourceFor(f.key); });
      await mergeListings({ source_ids: listings.map((l) => l.DaftId__c), keep_id: keepId, choices, image_from: imageSource });
      navigate('/duplicates');
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const radioCell = (name, id, selected, onSelect, content) => (
    <td key={id} className={`rec-merge-cell${selected ? ' rec-merge-selected' : ''}`}>
      <label className="rec-merge-label">
        <input type="radio" name={name} checked={selected} onChange={onSelect} />
        <span>{content}</span>
      </label>
    </td>
  );

  return (
    <>
      <div className="rec-header">
        <div>
          <h1 className="rec-edit-title">Merge listings</h1>
          <p className="rec-muted rec-sm rec-edit-subtitle">
            {eircode} · pick one value per row; the other listings are removed when you save
          </p>
        </div>
        <div className="rec-form-actions rec-form-actions-top">
          <button type="button" className="rec-btn-primary" disabled={saving} onClick={handleSave}>
            {saving ? 'Merging…' : 'Merge into one'}
          </button>
          <Link to="/duplicates" className="rec-btn">Cancel</Link>
        </div>
      </div>

      {error && <p className="rec-error rec-sm rec-banner">{error}</p>}

      <div className="rec-table-wrap">
        <table className="rec-table rec-merge-table">
          <thead>
            <tr>
              <th />
              {listings.map((l) => (
                <th key={l.DaftId__c}>
                  <Link to={`/listings/${encodeURIComponent(l.DaftId__c)}`} target="_blank" className="rec-row-link">{l.DaftId__c}</Link>
                  <div className="rec-muted rec-sm">Listed {l.Date_Listed__c ? l.Date_Listed__c.slice(0, 10) : '—'}</div>
                </th>
              ))}
              <th className="rec-merge-result-head">Merged record</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th className="rec-merge-field">Daft ID <span className="rec-muted rec-sm">(record to keep)</span></th>
              {listings.map((l) => radioCell('keep', l.DaftId__c, keepId === l.DaftId__c, () => setKeepId(l.DaftId__c), l.DaftId__c))}
              <td className="rec-merge-result"><strong>{keepId}</strong></td>
            </tr>

            {RECORD_FIELDS.map((f) => {
              const differing = differs(f.key);
              const source = sourceFor(f.key);
              return (
                <tr key={f.key} className={differing ? 'rec-merge-differs' : undefined}>
                  <th className="rec-merge-field">{f.label}</th>
                  {differing
                    ? listings.map((l) => radioCell(`f-${f.key}`, l.DaftId__c, source === l.DaftId__c, () => setPicked({ ...picked, [f.key]: l.DaftId__c }), show(l[f.key])))
                    : <td colSpan={listings.length} className="rec-merge-same rec-cell-wrap">{show(listings[0][f.key])} <span className="rec-muted rec-sm">(same in all)</span></td>}
                  <td className="rec-merge-result rec-cell-wrap">{show(byId[source][f.key])}</td>
                </tr>
              );
            })}

            <tr className={imageListings.length > 1 ? 'rec-merge-differs' : undefined}>
              <th className="rec-merge-field">Image</th>
              {listings.map((l) => {
                const thumb = l.has_image ? <img src={`/api/images/${encodeURIComponent(l.DaftId__c)}`} alt="" className="rec-merge-thumb" /> : <span className="rec-muted">No image</span>;
                return l.has_image && imageListings.length > 1
                  ? radioCell('image', l.DaftId__c, imageSource === l.DaftId__c, () => setPickedImage(l.DaftId__c), thumb)
                  : <td key={l.DaftId__c} className="rec-merge-cell">{thumb}</td>;
              })}
              <td className="rec-merge-result">
                {imageSource ? <img src={`/api/images/${encodeURIComponent(imageSource)}`} alt="" className="rec-merge-thumb" /> : <span className="rec-muted">No image</span>}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="rec-muted rec-sm rec-drop-note">
        Removed listings are copied to the merge archive first, and images that are replaced or left over are moved to a trash folder, not deleted.
      </p>
    </>
  );
}

export default function MergePage() {
  const { eircode } = useParams();
  const { data: group, loading, error } = useAsync(() => fetchDuplicateGroup(eircode), [eircode]);

  return (
    <div className="rec rec-page rec-wide">
      <Link to="/duplicates" className="rec-btn rec-back">
        <span aria-hidden="true">←</span> Back to duplicates
      </Link>
      {loading && <p className="rec-muted rec-sm">Loading…</p>}
      {error && <p className="rec-error rec-sm">Failed to load: {error.message}</p>}
      {group === null && <p className="rec-muted rec-sm">{eircode} no longer has duplicate listings.</p>}
      {group && <MergeForm eircode={group.eircode} listings={group.listings} />}
    </div>
  );
}
